import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import axios, { AxiosError } from 'axios'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>

    if (axiosError.response?.status === 404) {
      return 'User not found. They may have been deleted or you may not have permission to view them.'
    }

    if (axiosError.response?.status === 403) {
      return 'You do not have permission to view this user.'
    }

    if (axiosError.response?.status === 401) {
      return 'Your session has expired. Please log in again.'
    }

    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message
    }

    if (!axiosError.response && axiosError.code === 'ERR_NETWORK') {
      return 'Network error. Please check your internet connection and try again.'
    }
  }

  return error instanceof Error ? error.message : 'An unexpected error occurred'
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function formatNumber(value: string | number): string {
  const stringValue = value.toString()
  const isCurrency = stringValue.includes('$')
  const isPercentage = stringValue.includes('%')
  const numericValue = typeof value === 'string' ? parseFloat(value.replace(/[$,%]/g, '')) : value
  if (isNaN(numericValue)) {
    return stringValue
  }
  const formatted = numericValue.toLocaleString('en-US', {
    minimumFractionDigits: isCurrency ? 2 : 0,
    maximumFractionDigits: isCurrency ? 2 : isPercentage ? 0 : 2,
  })
  if (isCurrency) return `$${formatted}`
  if (isPercentage) return `${formatted}%`
  return formatted
}

export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000,
): Promise<T> {
  let retries = 0

  while (true) {
    try {
      return await fn()
    } catch (error) {
      if (retries >= maxRetries || !axios.isAxiosError(error) || error.response?.status !== 500) {
        throw error
      }

      const delay = baseDelay * Math.pow(2, retries)
      await sleep(delay)
      retries++
    }
  }
}

export function convertCentsToDollars(cents: number): number {
  return cents / 100
}
